import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * Marquee-style photo feed. Loops right-to-left, non-interactive
 * (pointer-events: none). Uses all wedding photos + featured moodboards.
 */
export default function PhotoFeed() {
  const [images, setImages] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/weddings").then((r) => r.data).catch(() => []),
      api.get("/moodboards").then((r) => r.data).catch(() => []),
    ]).then(([weddings, moodboards]) => {
      if (!mounted) return;
      const wedPhotos = weddings.flatMap((w) =>
        [w.cover_url, ...(w.photos || []).map((p) => p.url)].filter(Boolean)
      );
      const mbPhotos = moodboards.map((m) => m.image_url).filter(Boolean);
      // Shuffle a bit for visual variety
      const merged = [...wedPhotos, ...mbPhotos];
      const seen = new Set();
      const unique = merged.filter((u) => (seen.has(u) ? false : seen.add(u)));
      setImages(unique);
    });
    return () => { mounted = false; };
  }, []);

  const doubled = useMemo(() => [...images, ...images], [images]);

  if (!images.length) return null;

  return (
    <section
      data-testid="photo-feed"
      className="relative py-14 md:py-20 border-t border-[color:var(--border)] overflow-hidden select-none"
      aria-hidden="true"
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 mb-8 md:mb-12">
        <div className="eyebrow mb-2">The Feed</div>
        <h2 className="font-display italic text-3xl md:text-5xl leading-[1] tracking-tight">
          A running catalogue of moments.
        </h2>
      </div>

      <div className="relative pointer-events-none">
        {/* fade edges */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10 bg-gradient-to-r from-[color:var(--bg)] to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10 bg-gradient-to-l from-[color:var(--bg)] to-transparent" />

        <div className="flex ticker-feed w-max gap-3 md:gap-5">
          {doubled.map((src, i) => (
            <figure
              key={i}
              className="flex-shrink-0 h-[26vh] md:h-[38vh] aspect-[3/4] bg-[color:var(--surface)] overflow-hidden"
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                draggable="false"
                className="w-full h-full object-cover"
              />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
